import Vue, { PropType, VueConstructor } from 'vue';
import { Component, Prop } from 'vue-property-decorator';

interface IMixin<T, TErrors> {
	/**
	 * Readonly object of type T
	 */
	value: Readonly<T>;

	/**
	 * Readonly object of type TErrors
	 */
	errors: Readonly<TErrors>;

	/**
	 * Updates a single property of the value prop.
	 * @param key Key of property in value to update
	 * @param value The new value of the value prop at `key`
	 * @param errorKey Optional key in errors object to clear when updating the value. Defaults to `key`.
	 */
	$updateValue<K extends keyof T>(key: K, value: T[K], errorKey?: keyof T | keyof TErrors | null): void;

	/**
	 * 
	 * @param key Key of property in errors to update
	 * @param value The new error value at `key`
	 */
	$updateError<K extends keyof TErrors>(key: K, value: TErrors[K]): void;

	/**
	 * Alias for $emit('input', `value`)
	 * @param value Value to emit to the parent
	 */
	$emitValue(value: T): void;

	/**
	 * Alias for $emit('update:errors', `errors`)
	 * @param value Value to emit to the parent
	 */
	$emitErrors(errors: TErrors): void;
}

type IMixinWithAutoProperties<T, TErrors> = IMixin<T, TErrors> & {
	[K in keyof T]: Readonly<T[K]>;
};


/**
 * Creates a mixin that provides props and utility methods for updating immediately nested fields in an object property to be used as a v-model.
 */
function WithImmutableObjectValue<T, TErrors = Record<keyof T, string | null>>(): VueConstructor<Vue & IMixin<T, TErrors>>
/**
 * Creates a mixin that provides props and utility methods for updating immediately nested fields in an object property to be used as a v-model.
 * This overload generates auto get-set properties
 * 
 * @param keyGenerator Function that returns an object with null values for every key of T. Used for generating auto get-set properties.
 */
function WithImmutableObjectValue<T, TErrors = Record<keyof T, string | null>>(keyGenerator: (() => { [K in keyof T]: null})): VueConstructor<Vue & IMixinWithAutoProperties<T, TErrors>>
function WithImmutableObjectValue<T, TErrors = Record<keyof T, string | null>>(keyGenerator?: any): any {
	type ValueChange = { type: 'value', key: keyof T, value: T[keyof T] }
	type ErrorChange = { type: 'error', key: keyof TErrors, value: TErrors[keyof TErrors] }

	type Change = ValueChange | ErrorChange;

	function errorKeyExists(errors: TErrors, maybeKey: any): maybeKey is keyof TErrors {
		return errors && maybeKey in errors;
	}

	@Component
	class Mixin extends Vue implements IMixin<T, TErrors> {
		@Prop({ type: Object as PropType<T>, required: false })
		readonly value: Readonly<T>;

		@Prop({ type: Object as PropType<TErrors>, required: false, default: () => ({}) })
		readonly errors: Readonly<TErrors>;

		// Explicitly leaving this undefined so Vue does not make it reactive. Initialized in created.
		private changes: Change[];

		$updateValue<K extends keyof T>(key: K, value: T[K], errorKey: keyof T | keyof TErrors | null = key): void {
			if (this.changes == null) {
				console.warn(`[WithImmutableObjectValue warn]: attempting to update "${key}" before mixin created. This can be caused by assigning to "${key}" as a class field in your component.`);
			}

			if (this.value[key] === value) {
				return;
			}

			// If this is the first change in the queue, schedule a flush
			this.scheduleFlush();

			this.changes.push({ type: 'value', key, value });

			// Clear the associated error
			if (errorKey !== null && errorKeyExists(this.errors, errorKey)) {
				this.$updateError(errorKey, null);
			}
		}

		$updateError<K extends keyof TErrors>(key: K, value: TErrors[K]): void {
			this.scheduleFlush();
			this.changes.push({ type: 'error', key, value });
		}

		$emitValue(value: T): void {
			this.$emit('input', value);
		}

		$emitErrors(errors: TErrors): void {
			this.$emit('update:errors', errors);
		}

		private scheduleFlush(): void {
			if (!this.changes.length) {
				this.$nextTick(() => this.flushChanges());
			}
		}

		// Handles cases where may happen multiple times in the same tick.
		private flushChanges(): void {
			// Apply queued changes in order to the current `value` and/or `errors`
			const [newValue, newErrors] = this.changes.reduce(([value, errors], change) => {
				if (change.type === 'value') {
					value = {
						...value,
						[change.key]: change.value,
					};
				}

				if (change.type === 'error') {
					errors = {
						...errors,
						[change.key]: change.value,
					};
				}

				return [value, errors];
			}, [this.value, this.errors]);
			this.$emitValue(newValue);
			this.$emitErrors(newErrors);

			// Clear the change queue so we start fresh next time
			this.changes = [];
		}

		created(): void {
			this.changes = [];
		}
	}

	if (typeof keyGenerator !== 'function') {
		return Mixin;
	}

	// This section is not type safe. I'm not sure that there's a way to make it so, but the actual object generated is of the correct type.
	// The type assertion ensures that consumers will have type-safety, at least.
	return Vue.extend({
		mixins: [Mixin],
		computed: (Object.keys(keyGenerator()) as (keyof T)[]).reduce((computed, key) => {
			computed[key] = {
				get() {
					return this.value[key];
				},
				set(value: any) {
					this.$updateValue(key, value);
				}
			};

			return computed;
		}, {} as any)
	}) as VueConstructor<Vue & IMixinWithAutoProperties<T, TErrors>>;
}

export default WithImmutableObjectValue;